import { authApi } from '@/api/auth';
import { OTPTypesEnum } from '@/enums/OTPTypes';
import type {
    LoginRequest,
    LogoutRequest,
    ResendPhoneOTPRequest,
    VerifyPhoneOTPRequest,
} from '@/types/api/auth';
import {
    createGlobalState,
    createSharedComposable,
    useStorage,
} from '@vueuse/core';
import { usePost, useToaster } from '@foodics/ui-common';
import { computed, ref } from 'vue';
import { useResendTimer } from './useResendTimer';
import { isErrorOfCode, parseError } from '@/utils/errors';
import { useLocale } from './locale';

const USER_TOKEN_KEY = 'userToken';
const REQUEST_NEW_OTP_INTERVAL = 90;
const MAX_VERIFICATION_CODE_ATTEMPTS = 3;
export const PAY_OAUTH_REDIRECT_URL = '/oauth/pay/redirect';
export const currentUserToken = useStorage<string>(USER_TOKEN_KEY, null);
export const userPendingPhoneVerification = ref<{
    email: string;
    phone_dial_code: string;
    masked_phone_number: string;
} | null>(null);

export const usePhoneVerification = createSharedComposable(() => {
    const { addToast } = useToaster();
    const { secondsToWait, resetTimer, clearTimer, canResend } = useResendTimer(
        REQUEST_NEW_OTP_INTERVAL,
    );

    const remainingVerificationCodeAttempts = ref(
        MAX_VERIFICATION_CODE_ATTEMPTS,
    );

    const {
        execute: executeVerifyPhoneOTP,
        isLoading: isVerifyingPhoneOTP,
        error: verifyPhoneOTPError,
        onData: onPhoneOTPVerified,
    } = usePost<
        VerifyPhoneOTPRequest['payload'],
        VerifyPhoneOTPRequest['response']
    >(authApi.verifyPhoneOTP, { skipGlobalErrorHandler: true });
    async function verifyPhoneOTP(otp: string) {
        if (
            !userPendingPhoneVerification.value ||
            !remainingVerificationCodeAttempts.value
        )
            return false;

        try {
            remainingVerificationCodeAttempts.value -= 1;

            const { data, error } = await executeVerifyPhoneOTP({
                code: otp,
                email: userPendingPhoneVerification.value.email,
            });

            if (error) throw error;

            currentUserToken.value = data?.token;
            // userPendingPhoneVerification.value = null;
            return true;
        } catch (err: unknown) {
            if (isErrorOfCode(err, 'verification_attempts_exceeded')) {
                remainingVerificationCodeAttempts.value = 0;
            }

            return false;
        }
    }

    const {
        execute: executeResendPhoneOTP,
        isLoading: isResendingPhoneOTP,
        error: resendPhoneOTPError,
    } = usePost<
        ResendPhoneOTPRequest['payload'],
        ResendPhoneOTPRequest['response']
    >(authApi.resendPhoneOTP);
    async function resendOTP() {
        if (!canResend() || !userPendingPhoneVerification.value) return;

        try {
            resetTimer();
            remainingVerificationCodeAttempts.value =
                MAX_VERIFICATION_CODE_ATTEMPTS;

            const { error } = await executeResendPhoneOTP({
                email: userPendingPhoneVerification.value.email,
                type: OTPTypesEnum.VerifyPhone,
            });

            if (error) throw error;
        } catch (err: unknown) {
            const { t } = useLocale();
            const message = parseError(err);

            addToast({
                title: t('auth.verify_otp.reset_resend_otp_failed'),
                variant: 'error',
                description: message,
            });

            clearTimer();
        }
    }

    return {
        // verifying OTP
        verifyPhoneOTP,
        isVerifyingPhoneOTP,
        verifyPhoneOTPError,
        onPhoneOTPVerified,
        // resending OTP
        resendOTP,
        isResendingPhoneOTP,
        resendPhoneOTPError,
        // helpers
        remainingVerificationCodeAttempts,
        secondsToWaitBeforeRequestingNewOTP: secondsToWait,
    };
});

export const useAuth = createGlobalState(() => {
    const {
        execute: executeLogin,
        isLoading: isLoggingIn,
        error: loginError,
    } = usePost<LoginRequest['payload'], LoginRequest['response']>(
        authApi.login,
        { skipGlobalErrorHandler: true },
    );
    async function login(credentials: LoginRequest['payload']) {
        const { data, error } = await executeLogin(credentials);
        if (error || !data) return;

        userPendingPhoneVerification.value = {
            email: credentials.email,
            phone_dial_code: data.phone_dial_code,
            masked_phone_number: data.masked_phone_number,
        };
    }

    const isAuthenticated = computed(() => {
        return Boolean(currentUserToken.value);
    });

    const { execute: executeLogout, isLoading: isLoggingOut } = usePost<
        LogoutRequest['payload'],
        LogoutRequest['response']
    >(authApi.logout, { skipGlobalErrorHandler: true });
    async function logout() {
        await executeLogout();
        currentUserToken.value = null;
    }

    return {
        isAuthenticated,
        login,
        loginError,
        isLoggingIn,
        logout,
        isLoggingOut,
    };
});
